import {
  NWS_AFD_URL,
  NWS_MAPCLICK_URL,
  NWS_RADAR_LOCATION_URL,
  NWS_ZONE,
  SPC_TEXAS_OUTLOOK_HUB,
  SPC_TEXAS_OUTLOOK_IMAGES,
  WEATHER_LOCATION,
  WEATHER_TIMEZONE,
  WPC_QPF_HUB,
} from '../constants/weather';
import { fetchNwsForecastBundle, type NwsForecastBundle } from '../services/NwsForecastService';
import { escapeAttr, escapeHtml } from '../utils/escapeHtml';
import { formatAreaDiscussionUpdated, trimAreaDiscussionText } from '../utils/nwsAreaDiscussion';
import { groupForecastDays, groupHourlyPeriods, type NwsPeriod } from '../utils/nwsForecast';
import { WeatherRadarLoop } from './WeatherRadarLoop';

/** Live NWS-only weather page (10-day strip, hourly, maps moved off daily briefing). */
export class WeatherView {
  private abort: AbortController | null = null;
  private radarLoop: WeatherRadarLoop | null = null;

  render(container: HTMLElement): void {
    this.radarLoop?.stop();
    this.radarLoop = null;
    this.abort?.abort();
    this.abort = new AbortController();

    container.innerHTML = `
      <div class="weather-page">
        <header class="weather-page-header">
          <h2 class="file-title">Weather</h2>
          <p class="weather-page-lead">
            ${escapeHtml(WEATHER_LOCATION)} —
            <a href="${escapeAttr(NWS_MAPCLICK_URL)}" target="_blank" rel="noopener noreferrer">National Weather Service</a>
          </p>
        </header>
        <div class="weather-page-body" id="weather-page-body">
          ${this.radarSectionHtml()}
          <div id="weather-forecast-panel" class="weather-forecast-panel" aria-busy="true">
            <div class="weather-loading">
              <span class="app-spinner" aria-hidden="true"></span>
              <p>Loading NWS forecast…</p>
            </div>
          </div>
        </div>
      </div>`;

    const radarWrap = container.querySelector<HTMLElement>('#weather-radar-loop');
    if (radarWrap) {
      this.radarLoop = new WeatherRadarLoop(radarWrap, this.abort.signal);
      this.radarLoop.start();
    }

    void this.load(
      container.querySelector<HTMLElement>('#weather-forecast-panel')!,
      this.abort.signal
    );
  }

  private radarSectionHtml(): string {
    return `
      <section class="weather-section weather-radar-section" aria-labelledby="weather-radar-heading">
        <h3 class="weather-section-title" id="weather-radar-heading">
          <a href="${escapeAttr(NWS_RADAR_LOCATION_URL)}" target="_blank" rel="noopener noreferrer">NWS radar</a>
          <span class="weather-radar-subtitle"> — ${escapeHtml(WEATHER_LOCATION)}</span>
        </h3>
        <div class="weather-radar-wrap" id="weather-radar-loop" aria-busy="true">
          <div class="weather-loading weather-radar-loading">
            <span class="app-spinner" aria-hidden="true"></span>
          </div>
        </div>
      </section>`;
  }

  private async load(panel: HTMLElement, signal: AbortSignal): Promise<void> {
    try {
      const bundle = await fetchNwsForecastBundle();
      if (signal.aborted) return;
      panel.innerHTML = this.buildForecastHtml(bundle);
      panel.removeAttribute('aria-busy');
    } catch (err) {
      if (signal.aborted) return;
      panel.innerHTML = `
        <div class="weather-error">
          <p>Could not load weather from NWS: ${escapeHtml((err as Error).message)}</p>
          <p class="text-muted small">Try again in a moment, or open the
            <a href="${escapeAttr(NWS_MAPCLICK_URL)}" target="_blank" rel="noopener noreferrer">NWS forecast</a>.
          </p>
        </div>`;
      panel.removeAttribute('aria-busy');
    }
  }

  private buildForecastHtml(bundle: NwsForecastBundle): string {
    const days = groupForecastDays(bundle.forecastPeriods, WEATHER_TIMEZONE);
    const hours = groupHourlyPeriods(bundle.hourlyPeriods, WEATHER_TIMEZONE);
    const alerts = bundle.alerts.length ? this.alertsHtml(bundle.alerts) : '';
    const dayStrip = days.map(d => this.dayCardHtml(d)).join('');
    const hourStrip = hours.map(h => this.hourCardHtml(h)).join('');
    const spc = this.spcMapsHtml();
    const wpc = this.wpcMapsHtml(bundle.wpcMaps);
    const discussion = this.areaDiscussionHtml(bundle.areaDiscussion);
    const detail = this.detailForecastHtml(bundle.forecastPeriods);

    return `
      ${alerts}
      <section class="weather-section" aria-labelledby="weather-hourly-heading">
        <h3 class="weather-section-title" id="weather-hourly-heading">
          <span class="weather-section-icon" aria-hidden="true">🕐</span> Hourly forecast
        </h3>
        <div class="weather-scroll-row" tabindex="0">${hourStrip}</div>
      </section>
      <section class="weather-section" aria-labelledby="weather-10day-heading">
        <h3 class="weather-section-title" id="weather-10day-heading">
          <span class="weather-section-icon" aria-hidden="true">📅</span> ${days.length}-day forecast
        </h3>
        <div class="weather-scroll-row weather-scroll-row-days" tabindex="0">${dayStrip}</div>
      </section>
      ${spc}
      ${wpc}
      ${discussion}
      ${detail}`;
  }

  private alertsHtml(alerts: NwsForecastBundle['alerts']): string {
    const items = alerts
      .map(
        a => `<li><strong>${escapeHtml(a.event)}</strong> — ${escapeHtml(a.headline)}</li>`
      )
      .join('');
    return `<div class="weather-alerts" role="alert"><ul>${items}</ul></div>`;
  }

  private hourCardHtml(h: ReturnType<typeof groupHourlyPeriods>[number]): string {
    const precip =
      h.precipChance != null ? `<span class="weather-precip">${h.precipChance}%</span>` : '';
    return `
      <article class="weather-hour-card">
        <p class="weather-hour-temp">${h.tempF}°</p>
        <img class="weather-icon" src="${escapeAttr(h.iconUrl)}" alt="" width="40" height="40" loading="lazy" decoding="async" />
        ${precip}
        <p class="weather-hour-label">${escapeHtml(h.label)}</p>
      </article>`;
  }

  private dayCardHtml(d: ReturnType<typeof groupForecastDays>[number]): string {
    const high = d.highF != null ? `<p class="weather-day-high">${d.highF}°</p>` : '';
    const low = d.lowF != null ? `<p class="weather-day-low">${d.lowF}°</p>` : '';
    const precip =
      d.precipChance != null ? `<span class="weather-precip">${d.precipChance}%</span>` : '';
    return `
      <article class="weather-day-card">
        ${high}
        ${low}
        <img class="weather-icon" src="${escapeAttr(d.iconUrl)}" alt="${escapeAttr(d.shortForecast)}" width="48" height="48" loading="lazy" decoding="async" />
        ${precip}
        <p class="weather-day-name">${escapeHtml(d.dayLabel)}</p>
        <p class="weather-day-date">${escapeHtml(d.dateLabel)}</p>
      </article>`;
  }

  private spcMapsHtml(): string {
    const cells = SPC_TEXAS_OUTLOOK_IMAGES.map(
      img => `
        <figure class="weather-map-cell">
          <img src="${escapeAttr(img.url)}" alt="Texas severe outlook — ${escapeAttr(img.label)}" class="weather-map-img" loading="lazy" decoding="async" />
          <figcaption>${escapeHtml(img.label)}</figcaption>
        </figure>`
    ).join('');
    return `
      <section class="weather-section" aria-labelledby="weather-spc-heading">
        <h3 class="weather-section-title" id="weather-spc-heading">
          <a href="${escapeAttr(SPC_TEXAS_OUTLOOK_HUB)}" target="_blank" rel="noopener noreferrer">SPC Texas outlooks</a>
        </h3>
        <div class="weather-map-row">${cells}</div>
      </section>`;
  }

  private wpcMapsHtml(maps: NwsForecastBundle['wpcMaps']): string {
    const cells = maps
      .map(
        m => `
        <figure class="weather-map-cell">
          <img src="${escapeAttr(m.url)}" alt="${escapeAttr(m.label)}" class="weather-map-img" loading="lazy" decoding="async" />
          <figcaption>${escapeHtml(m.label)}</figcaption>
        </figure>`
      )
      .join('');
    return `
      <section class="weather-section" aria-labelledby="weather-wpc-heading">
        <h3 class="weather-section-title" id="weather-wpc-heading">
          <a href="${escapeAttr(WPC_QPF_HUB)}" target="_blank" rel="noopener noreferrer">WPC rainfall outlook (5 &amp; 7 day)</a>
        </h3>
        <div class="weather-map-row weather-map-row-2">${cells}</div>
      </section>`;
  }

  private areaDiscussionHtml(discussion: NwsForecastBundle['areaDiscussion']): string {
    if (!discussion) return '';

    const trimmed = trimAreaDiscussionText(discussion.productText);
    if (!trimmed) return '';

    const updated = formatAreaDiscussionUpdated(discussion.issuanceTime, WEATHER_TIMEZONE);
    const updatedLabel = updated ? ` · updated ${updated}` : '';

    return `
      <section class="weather-section weather-discussion-section">
        <details class="weather-detail">
          <summary>NWS area forecast discussion (${escapeHtml(NWS_ZONE)}${escapeHtml(updatedLabel)})</summary>
          <div class="weather-detail-body weather-discussion-body">
            <p class="weather-discussion-source">
              <a href="${escapeAttr(NWS_AFD_URL)}" target="_blank" rel="noopener noreferrer">Full discussion on weather.gov</a>
            </p>
            ${escapeHtml(trimmed)}
          </div>
        </details>
      </section>`;
  }

  private detailForecastHtml(periods: NwsPeriod[]): string {
    const rows = periods
      .map(
        p =>
          `<p class="weather-detail-period"><strong>${escapeHtml(p.name)}:</strong> ${escapeHtml(p.detailedForecast || p.shortForecast)}</p>`
      )
      .join('');
    return `
      <section class="weather-section weather-detail-section">
        <details class="weather-detail">
          <summary>NWS detailed forecast (${escapeHtml(WEATHER_LOCATION.split(',')[0] ?? 'property')})</summary>
          <div class="weather-detail-body">${rows}</div>
        </details>
      </section>`;
  }
}
